// The closed catalog of FLOOR mutations (spec 06 §4.1/§4.2 floor, §4.4 Transfer
// Receive, §4.5 Count Worksheet — plus, since 04-B-3, module 04's
// `production_event` kind on the SAME queue: D-048/D-054 rule one outbox, so
// production extends this catalog rather than standing up a second engine)
// and how each one reaches the API.
//
// ── 07-A: MODULE 07 JOINS THE ONE QUEUE, IT DOES NOT FORK IT ────────────────
//
// The floor half of module 07 (spec 07 §4.4's execution screen) queues two
// mutations — one check-item result and one completion — and it queues them
// HERE, in this catalog, on the `assessly.floor` database at version 2, beside
// the module-04 binary/photo queue D-096 built. Nothing about the storage layer
// moves: `OUTBOX_DB_NAME`, `OUTBOX_DB_VERSION`, `OUTBOX_STORE_NAME` and
// `PHOTO_STORE_NAME` are untouched by this slice, and `outbox-store.test.ts` /
// `photo-store.test.ts` / `floor-outbox-photos.test.ts` pass byte-for-byte as
// they were written for module 04.
//
// That is the whole of the "promotion": the shared floor lib is `lib/` in this
// directory, and it has been shared since 04-B-3 put production's kind on
// module 06's queue and 05-B put waste's on it. A SECOND IndexedDB store for
// quality photos would mean a device holding two independent blob queues under
// two independent lifecycles — a photo uploaded twice (two records, two
// registry rows, two `photo_file_ids` the applier cannot reconcile) or a photo
// dropped when one queue's `clear()` ran and the other's did not. D-048/D-054
// already forbade the second ENGINE; this is the same argument one layer down,
// and the single fact that proves it is that `PHOTO_STORE_NAME` appears in
// exactly one file and `OUTBOX_DB_VERSION` is still 2.
//
// What module 07 DID need from the shared lib, and got additively:
//   * `FloorEvidencePurpose` gains `quality_evidence` (photo-store.ts) — the
//     registry purpose spec 07 §2.6 names. The record round-trip validates it,
//     because a purpose silently dropped on read is the 2026-08-09 defect that
//     parked every waste capture (see `photoFromRecord`).
//   * `resolveOutcome` gains a quality branch (below): a 2xx is INTERPRETED,
//     never assumed to be an application (D-080).
//   * `REPLAN_CODES` gains the two quality "the world moved" refusals, so the
//     tray offers "open it and look" rather than a Retry that can never win.
// Module 04's specs assert module 04's kinds and module 04's codes; every one
// of those additions is disjoint from what they assert.
//
// One place, for three reasons:
//
//  * The outbox engine stays transport-agnostic. It knows how to persist,
//    order, retry and park an operation; it does not know what a cut is. When
//    D-048's transport moves to Module 18's sync protocol, `send()` below is
//    the only function that changes — the replay policy and the domain
//    idempotency (which §9.2 says stay in module 06) do not move at all.
//  * Every mutation goes through `@assessly/api-client` (doctrine 3). There is
//    no hand-rolled fetch anywhere in the floor slice.
//  * The `Idempotency-Key` is supplied by the CALLER (the engine) rather than
//    minted here. That is the §9.2 hard invariant expressed in the type system:
//    `send()` physically cannot mint a key, so no code path can regenerate one
//    on a retry and double-apply a cut.
//
// A note on what is NOT here: `POST /cycle-counts/{id}/start` and the review /
// approve actions. Starting a count snapshots stock (§3.4 step 2) and approving
// posts permanent movements — office decisions, made by a storekeeper or
// facility_admin on a connected screen (§4.5). A floor device queues COUNTS,
// never the gate around them.

import type { components } from "@assessly/api-client";
import { client } from "../../../lib/api";
import { unwrap } from "../../../lib/request";

type S = components["schemas"];

export type ScanRequest = S["ScanRequest"];
export type ScanResponse = S["ScanResponse"];
export type CutRequest = S["CutRequest"];
export type CutResponse = S["CutResponse"];
export type CountLineCountRequest = S["CountLineCountRequest"];
export type CountLineAddRequest = S["CountLineAddRequest"];
export type CycleCountDetailResponse = S["CycleCountDetailResponse"];
export type TransferReceiveRequest = S["TransferReceiveRequest"];
export type TransferReceiveResponse = S["TransferReceiveResponse"];
export type RemeasureResponse = S["RemeasureResponse"];
export type FloorEventsBatchResponse = S["FloorEventsBatchResponse"];

/**
 * One §5.1 production floor event as the DEVICE persists it at tap time —
 * module 04's op kind on module 06's ONE queue (D-048/D-054, D-080).
 *
 * `client_event_id` is deliberately NOT here: it is the outbox record's
 * `clientKey` (uuid v7 minted at TAP by the engine), injected by `sendFloorOp`
 * from the caller's idempotency key, so `clientKey === client_event_id` holds
 * by construction and no code path can send a different id on a retry.
 *
 * `occurred_at` IS here: stamped at TAP from the device clock, persisted with
 * the body, sent VERBATIM on every attempt — the server preserves it and §9.6
 * attribution clamps it. Re-stamping on retry would move a night-shift tap
 * into the morning drain.
 */
export type ProductionEventBody = Omit<S["FloorBatchEvent"], "client_event_id">;

/**
 * Module 05's floor captures on the SAME queue (slice 05-B — the outbox's
 * generalization to a SECOND endpoint family, the work item spec 05 §9's
 * BUILT notes name; modules 07/12 inherit this shape). `client_event_id` is
 * omitted for the same reason as the production body above: it is the record's
 * `clientKey`, injected by `sendFloorOp` from the caller's idempotency key, so
 * the §9.1 dedupe key can never drift from the retry key.
 */
export type WasteEventBody = Omit<S["WasteEventCreateRequest"], "client_event_id">;
export type WasteRemnantBody = Omit<S["RemnantRegisterRequest"], "client_event_id">;

/**
 * Module 07's two floor mutations (spec 07 §5, slice 07-A) on the SAME queue.
 *
 * NEITHER carries a client key field, and that is the contract rather than an
 * omission. `POST /inspections/:id/results` has no `client_idempotency_key`
 * COLUMN: the write is an UPSERT on `(inspection_id, check_item_id)` — §2.6's
 * identity — so a device that flushes the same result twice converges on one
 * row, and the platform's `Idempotency-Key` header (the engine's tap-time
 * `clientKey`) carries the response replay. Completion is the same shape.
 *
 * `photo_file_ids` is persisted EMPTY at tap and bound at SEND time from the
 * durable blob queue (`attachPhotoFileIds` below, D-096) — the flag's evidence
 * is uploaded before the event, under the flag's own key, so a replay after a
 * dropped response carries byte-identical ids.
 */
export type QualityResultBody = S["QualityInspectionResultRequest"];
export type QualityCompleteBody = S["QualityInspectionCompleteRequest"];

/**
 * Module 09's five shipment doors on the SAME queue (spec 09 §3.7 / §4.8,
 * P3-D PR-D3, D-286) — the waste pair's "plain idempotent POSTs" posture, not
 * D-080's batch envelope: every one of them answers `ShipmentDetailResponse`
 * (open and pick with 201, the rest 200), a replay under the same
 * `Idempotency-Key` answers the same body and posts nothing twice, and a
 * refusal is a non-2xx that the drain classifies like any other. The record
 * carries `orderId` on every kind, even where the wire does not take it,
 * because the pick screen is addressed by ORDER (`/floor/ship/$orderId`) and
 * a parked row that cannot name its order cannot be opened from the tray.
 *
 * OFFLINE OPEN-THEN-PICK IS NOT CHAINED as a follow-up: the pick screen's
 * first tap opens ONLINE (the queue itself is a cached read and the pick plan
 * only exists on an open shipment), and the §5.4 offline leg queues picks
 * against a shipment that already exists.
 */
export type OpenShipmentBody = S["OpenShipmentRequest"];
export type PickBody = S["PickRequest"];
export type ShipBody = S["ShipRequest"];
export type ShipmentDetailResponse = S["ShipmentDetailResponse"];
export type ShipmentListResponse = S["ShipmentListResponse"];

/**
 * A queued floor operation. Every variant carries the FULL request body: the
 * record persisted at tap time must be sufficient to replay the mutation on
 * its own, because the screen that created it is long gone by the time a dead
 * zone ends.
 */
export type FloorOp =
  | { readonly kind: "scan"; readonly body: ScanRequest }
  | { readonly kind: "cut"; readonly unitId: string; readonly body: CutRequest }
  | {
      readonly kind: "remeasure";
      readonly unitId: string;
      readonly body: { readonly entered_measure: string };
    }
  | {
      readonly kind: "count_record";
      readonly countId: string;
      readonly lineId: string;
      readonly body: CountLineCountRequest;
    }
  | {
      readonly kind: "count_found";
      readonly countId: string;
      readonly body: CountLineAddRequest;
    }
  | { readonly kind: "count_submit"; readonly countId: string }
  | {
      readonly kind: "transfer_receive";
      readonly transferId: string;
      readonly body: TransferReceiveRequest;
    }
  | {
      readonly kind: "production_event";
      /** The registered device (§2.16 registry — context/telemetry, never an
       * auth factor, D-079) riding the batch ENVELOPE. */
      readonly deviceId: string;
      readonly event: ProductionEventBody;
    }
  // Module 05 slice 05-B (spec 05 §4 S8/S9, §9.1): plain idempotent POSTs, not
  // the batch envelope — the waste endpoints take ONE event and answer with
  // `idempotent_replay` on the body, so their 2xx IS the application (the
  // module-06 settlement contract, not D-080's per-event results[]).
  | { readonly kind: "waste_event"; readonly body: WasteEventBody }
  | { readonly kind: "waste_remnant"; readonly body: WasteRemnantBody }
  // Module 07 slice 07-A (spec 07 §4.4, §9.1/§9.8): the execution screen's two
  // writes. Plain idempotent POSTs like the waste pair — not D-080's batch
  // envelope — but their 2xx is still INTERPRETED before anything leaves the
  // queue (`resolveOutcome`), because a 200 that does not mention the check
  // item we recorded is not evidence that it was recorded.
  | {
      readonly kind: "quality_result";
      readonly inspectionId: string;
      readonly body: QualityResultBody;
    }
  | {
      readonly kind: "quality_complete";
      readonly inspectionId: string;
      readonly body: QualityCompleteBody;
    }
  // Module 09 P3-D PR-D3 (spec 09 §3.7 / §4.8, D-286): the floor's shipment
  // doors. `orderId` rides every record so the tray can point at the pick
  // screen; `body` is the FULL wire body, persisted at tap time.
  | {
      readonly kind: "shipment_open";
      readonly orderId: string;
      readonly body: OpenShipmentBody;
    }
  | {
      readonly kind: "shipment_pick";
      readonly orderId: string;
      readonly shipmentId: string;
      readonly body: PickBody;
    }
  | {
      readonly kind: "shipment_unpick";
      readonly orderId: string;
      readonly shipmentId: string;
      /** The `shipment_lines` row id (NOT the order line). */
      readonly lineId: string;
    }
  | { readonly kind: "shipment_pack"; readonly orderId: string; readonly shipmentId: string }
  | {
      readonly kind: "shipment_ship";
      readonly orderId: string;
      readonly shipmentId: string;
      readonly body: ShipBody;
    };

export type FloorOpKind = FloorOp["kind"];

// ---------------------------------------------------------------------------
// Follow-ups: an operation whose INPUT is another operation's answer
// ---------------------------------------------------------------------------

/**
 * §4.5 "found something not on the list" is the one floor motion that is two
 * calls, not one: the operator SCANS the extra bobbin, and only the server can
 * say which stock unit that label is (§9.9 — the client never resolves a
 * payload). The count line (§5 row 41, `POST /cycle-counts/{id}/lines`) can then
 * be created, and not before.
 *
 * Chaining them in the screen would work only while the screen is mounted and
 * only while there is signal — which is to say, not on a floor. So the INTENT is
 * persisted next to the scan in the outbox record: when the scan finally lands,
 * hours later, out of a dead zone, possibly after a restart, the engine derives
 * the line-add from the response and queues it. That is why "the item is added
 * to the count once the label is recognised" is a true sentence and not a
 * hopeful one.
 */
export interface FloorFollowUp {
  readonly kind: "count_found";
  readonly countId: string;
  readonly locationId: string;
}

/** Read a persisted follow-up back, refusing anything malformed. */
export function parseFollowUp(value: unknown): FloorFollowUp | undefined {
  if (value === null || typeof value !== "object") return undefined;
  const record = value as Record<string, unknown>;
  if (record["kind"] !== "count_found") return undefined;
  const countId = record["countId"];
  const locationId = record["locationId"];
  if (typeof countId !== "string" || countId.length === 0) return undefined;
  if (typeof locationId !== "string" || locationId.length === 0) return undefined;
  return { kind: "count_found", countId, locationId };
}

/**
 * The operation a settled response unlocks, or `undefined` when it unlocks
 * nothing. A scan that resolved to a location, a lot or an unknown label does
 * NOT add a count line — only a stock unit does, and inventing a line from
 * anything else would put a phantom row on a worksheet.
 */
export function deriveFollowUp(followUp: FloorFollowUp, response: unknown): FloorOp | undefined {
  const scan = response as Partial<ScanResponse> | undefined;
  if (scan?.resolved_kind !== "stock_unit") return undefined;
  const unit = scan.stock_unit ?? undefined;
  if (unit === undefined) return undefined;
  return {
    kind: "count_found",
    countId: followUp.countId,
    body: { location_id: followUp.locationId, stock_unit_id: unit.id },
  };
}

/**
 * Bind the UPLOADED evidence-photo file ids onto a queued flag at SEND time
 * (D-096). The record persisted at tap time carries `photo_file_ids: []` plus
 * the device-side photo ids in `OutboxItem.photoIds`; the drain resolves those
 * through the three-call upload and composes the final payload here — under
 * the flag's own tap-time `client_event_id`, so a replay after a dropped
 * response carries byte-identical ids and dedupes (§3.4). A non-production op
 * (nothing else carries photos today) is returned unchanged.
 */
export function attachPhotoFileIds(op: FloorOp, fileIds: readonly string[]): FloorOp {
  if (op.kind === "quality_result") {
    // Spec 07 §2.6's `photo_file_ids`, purpose `quality_evidence`. The applier
    // validates each id against the registry (same tenant, right purpose,
    // `status='stored'`) and 422s `QUALITY_EVIDENCE_INVALID` naming the id, so
    // binding anything the uploader did not actually store would park the
    // result rather than lose it — but it would park it on a refusal the
    // operator cannot act on, which is why the ids come from the upload's own
    // answer and never from the capture.
    return { ...op, body: { ...op.body, photo_file_ids: [...fileIds] } };
  }
  if (op.kind === "waste_event") {
    // S8's evidence photo (spec 05 §2.3 `photo_file_ids`, purpose
    // `waste_evidence` — D-064 pt 8): same upload-first binding as the flag,
    // under the capture's own tap-time client_event_id.
    return { ...op, body: { ...op.body, photo_file_ids: [...fileIds] } };
  }
  if (op.kind !== "production_event") return op;
  return {
    ...op,
    event: { ...op.event, payload: { ...op.event.payload, photo_file_ids: [...fileIds] } },
  };
}

/**
 * Issue one operation with the caller's key. Returns the decoded response, or
 * throws the shared `RequestError` (problem code, http status, request id) the
 * engine classifies.
 */
export async function sendFloorOp(op: FloorOp, idempotencyKey: string): Promise<unknown> {
  const header = { "Idempotency-Key": idempotencyKey } as const;
  switch (op.kind) {
    case "scan":
      return unwrap(
        await client.POST("/v1/inventory/scan", { params: { header }, body: op.body }),
        "scan",
      );
    case "cut":
      return unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/cut", {
          params: { path: { id: op.unitId }, header },
          body: op.body,
        }),
        "cut",
      );
    case "remeasure":
      return unwrap(
        await client.POST("/v1/inventory/stock-units/{id}/remeasure", {
          params: { path: { id: op.unitId }, header },
          body: op.body,
        }),
        "remeasure",
      );
    case "count_record":
      return unwrap(
        await client.PUT("/v1/inventory/cycle-counts/{id}/lines/{lineId}/count", {
          params: { path: { id: op.countId, lineId: op.lineId }, header },
          body: op.body,
        }),
        "count-record",
      );
    case "count_found":
      return unwrap(
        await client.POST("/v1/inventory/cycle-counts/{id}/lines", {
          params: { path: { id: op.countId }, header },
          body: op.body,
        }),
        "count-found",
      );
    case "count_submit":
      return unwrap(
        await client.POST("/v1/inventory/cycle-counts/{id}/submit", {
          params: { path: { id: op.countId }, header },
        }),
        "count-submit",
      );
    case "transfer_receive":
      return unwrap(
        await client.POST("/v1/inventory/transfers/{id}/receive", {
          params: { path: { id: op.transferId }, header },
          body: op.body,
        }),
        "transfer-receive",
      );
    case "production_event":
      // BATCH-OF-1 (D-080): the envelope takes `events[]` so true batching
      // later is a transport change, not a wire change — but 04-B sends one
      // event per POST, because the drain is strict FIFO and a multi-event
      // batch would need per-event settle bookkeeping the engine does not
      // have. `client_event_id` is the CALLER's key — the record's primary
      // key — injected here so it can never drift from the `Idempotency-Key`
      // and never be re-minted on a retry (§3.4: UNIQUE (tenant_id,
      // client_event_id) is THE dedupe).
      return unwrap(
        await client.POST("/v1/floor/events:batch", {
          params: { header },
          body: {
            device_id: op.deviceId,
            events: [{ ...op.event, client_event_id: idempotencyKey }],
          },
        }),
        "floor-events",
      );
    case "waste_event":
      // `client_event_id` = the caller's key (spec 05 §9.1: UNIQUE (tenant,
      // client_event_id) is THE dedupe; a replay returns the ORIGINAL row).
      return unwrap(
        await client.POST("/v1/waste/events", {
          params: { header },
          body: { ...op.body, client_event_id: idempotencyKey },
        }),
        "waste-event",
      );
    case "waste_remnant":
      return unwrap(
        await client.POST("/v1/waste/remnants", {
          params: { header },
          body: { ...op.body, client_event_id: idempotencyKey },
        }),
        "waste-remnant",
      );
    case "quality_result":
      // No client key in the BODY (spec 07 §2.6 has no column for one): the
      // UPSERT on (inspection_id, check_item_id) is the row-level dedupe and
      // the header carries the response replay.
      return unwrap(
        await client.POST("/v1/quality/inspections/{id}/results", {
          params: { path: { id: op.inspectionId }, header },
          body: op.body,
        }),
        "quality-result",
      );
    case "quality_complete":
      return unwrap(
        await client.POST("/v1/quality/inspections/{id}/complete", {
          params: { path: { id: op.inspectionId }, header },
          body: op.body,
        }),
        "quality-complete",
      );
    // Module 09 (spec 09 §5, PR-D3): the header carries the tap-time key on
    // every one of these, the DELETE included — the register's `route()` adds
    // `Idempotency-Key` to every non-public mutation whatever its verb.
    case "shipment_open":
      return unwrap(
        await client.POST("/v1/orders/{id}/shipments", {
          params: { path: { id: op.orderId }, header },
          body: op.body,
        }),
        "shipment-open",
      );
    case "shipment_pick":
      return unwrap(
        await client.POST("/v1/shipments/{id}/lines", {
          params: { path: { id: op.shipmentId }, header },
          body: op.body,
        }),
        "shipment-pick",
      );
    case "shipment_unpick":
      return unwrap(
        await client.DELETE("/v1/shipments/{id}/lines/{lineId}", {
          params: { path: { id: op.shipmentId, lineId: op.lineId }, header },
        }),
        "shipment-unpick",
      );
    case "shipment_pack":
      return unwrap(
        await client.POST("/v1/shipments/{id}/pack", {
          params: { path: { id: op.shipmentId }, header },
        }),
        "shipment-pack",
      );
    case "shipment_ship":
      return unwrap(
        await client.POST("/v1/shipments/{id}/ship", {
          params: { path: { id: op.shipmentId }, header },
          body: op.body,
        }),
        "shipment-ship",
      );
  }
}

// ---------------------------------------------------------------------------
// Response interpretation (D-080 — the settlement contract)
// ---------------------------------------------------------------------------

/**
 * How ONE drained operation actually ended, read out of a 2xx response.
 *
 * Endpoint 13 answers HTTP 200 with a PER-EVENT status — a `conflicted` event
 * arrives inside a successful POST. Before D-080 the engine treated any 2xx as
 * "applied", so a rejected qty report was deleted from the queue and shown as
 * a green tick: the operator's floor truth vanished. Every drain now runs the
 * response through this interpreter BEFORE deleting anything.
 */
export type OpResolution =
  | { readonly kind: "applied" }
  /** This `client_event_id` was already applied — a replay after a dropped
   * response. The original result stands, zero projection deltas (§3.4). */
  | { readonly kind: "deduplicated" }
  /** The server persisted the event into `floor_sync_conflicts` and applied
   * nothing. The op must leave the send queue (a retry would only dedupe) but
   * NEVER settle as success — it becomes a `needs_review` record. */
  | { readonly kind: "conflicted"; readonly reason: string };

/**
 * SYNTHETIC conflict reasons, minted client-side when the response cannot be
 * trusted to say what happened. Deliberately lower_snake like the server's
 * `floor_sync_conflicts.reason` values so one rendering path handles both;
 * deliberately NOT values the server emits, so a log line names its side.
 */
export const REVIEW_REASON_RESULT_MISSING = "result_missing";
export const REVIEW_REASON_RESULT_UNRECOGNIZED = "result_unrecognized";
/**
 * The 2xx that says the inspection is CANCELLED (spec 07 §4.4's sync-conflict
 * state: "inspection cancelled server-side"). Distinct from the two above
 * because it is not a malformed answer — it is a well-formed answer describing
 * a world the queued tap no longer fits, and the operator's next move is to
 * look at the inspection rather than to re-send anything.
 */
export const REVIEW_REASON_INSPECTION_CANCELLED = "quality_inspection_cancelled";

/**
 * Interpret a 2xx response for the operation it settled.
 *
 * Module-06 kinds keep their existing contract — the endpoint's 2xx IS the
 * application (failures are non-2xx and never reach here). For
 * `production_event`, the batch response's `results[]` is matched on
 * `client_event_id === clientKey`; a MISSING or unmatchable result is
 * `conflicted` with a synthetic reason, NEVER `applied` — claiming success on
 * an answer that does not mention the event is exactly the silent loss D-080
 * exists to prevent.
 */
export function resolveOutcome(op: FloorOp, clientKey: string, response: unknown): OpResolution {
  if (op.kind === "quality_result") return resolveQualityResult(op, response);
  if (op.kind === "quality_complete") return resolveQualityCompletion(response);
  // Module 09's shipment doors keep the module-06/waste contract: the 2xx IS
  // the application. Every one of them answers the shipment itself, a replay
  // under the same key answers the same body, and a refusal is a non-2xx —
  // there is no per-event status inside a 200 to interpret (D-286).
  if (op.kind !== "production_event") return { kind: "applied" };
  const results = (response as { results?: unknown } | null | undefined)?.results;
  const list: unknown[] = Array.isArray(results) ? results : [];
  const match = list.find(
    (row): row is Record<string, unknown> =>
      row !== null &&
      typeof row === "object" &&
      (row as Record<string, unknown>)["client_event_id"] === clientKey,
  );
  if (match === undefined) return { kind: "conflicted", reason: REVIEW_REASON_RESULT_MISSING };
  const status = match["status"];
  if (status === "applied") return { kind: "applied" };
  if (status === "deduplicated") return { kind: "deduplicated" };
  if (status === "conflicted") {
    const reason = match["conflict_reason"];
    return {
      kind: "conflicted",
      reason:
        typeof reason === "string" && reason.length > 0
          ? reason
          : REVIEW_REASON_RESULT_UNRECOGNIZED,
    };
  }
  return { kind: "conflicted", reason: REVIEW_REASON_RESULT_UNRECOGNIZED };
}

/**
 * MODULE 07's 2xx, interpreted (spec 07 §5, D-080's rule generalised).
 *
 * The quality endpoints are plain POSTs — no per-event `results[]` — so it is
 * tempting to read "2xx ⇒ applied" straight off the status line, as the
 * module-06 kinds legitimately do. That inference is only sound when the answer
 * DESCRIBES what was applied, and here it does: `POST /results` echoes the
 * stored row with its COMPUTED verdict, and the row names its check item. So
 * the same discipline applies, one field down: a 200 whose `result.check_item_id`
 * is not the item this tap recorded is an answer about something else, and
 * deleting the record on it would be the silent loss D-080 exists to prevent —
 * the operator's measurement gone under a green tick, with the inspection still
 * short one verdict at completion time.
 *
 * The verdict itself is NOT a conflict, whatever it says. A measurement of
 * 2.31 mm against a 2.10 mm ceiling comes back `fail` and that is the write
 * SUCCEEDING (§3.4's auto-grading). The screen renders the computed verdict; it
 * never renders a graded fail as an error, and never as a pass (M07-S09).
 */
function resolveQualityResult(
  op: Extract<FloorOp, { kind: "quality_result" }>,
  response: unknown,
): OpResolution {
  const row = (response as { result?: unknown } | null | undefined)?.result;
  if (row === null || row === undefined || typeof row !== "object") {
    return { kind: "conflicted", reason: REVIEW_REASON_RESULT_MISSING };
  }
  const recorded = (row as Record<string, unknown>)["check_item_id"];
  if (recorded !== op.body.check_item_id) {
    return { kind: "conflicted", reason: REVIEW_REASON_RESULT_UNRECOGNIZED };
  }
  const status = (response as { inspection_status?: unknown }).inspection_status;
  // A 200 that reports the inspection CANCELLED cannot have recorded a verdict
  // on it (§3.4 has no such transition), so it is a review note, not a tick.
  if (status === "cancelled") {
    return { kind: "conflicted", reason: REVIEW_REASON_INSPECTION_CANCELLED };
  }
  return { kind: "applied" };
}

/**
 * A COMPLETION's 2xx (spec 07 §3.4). `passed` and `failed` are the only two
 * statuses a completion can produce, so anything else — `open`, `in_progress`,
 * a missing inspection object, `cancelled` — is a 200 that did not complete
 * anything, and settling it as success would tell an operator their inspection
 * is closed while the queue that could still close it has been emptied.
 *
 * The TWO-DEVICE RACE of §9.1 does not arrive here: the loser gets a 409
 * `QUALITY_INSPECTION_ALREADY_COMPLETED`, which is a refusal and goes through
 * the drain's classifier — parked, with `parkedAdvice` reading `replan` (see
 * REPLAN_CODES) so the tray says "open it and look at what is there" instead of
 * offering a Retry that can never win.
 */
function resolveQualityCompletion(response: unknown): OpResolution {
  const inspection = (response as { inspection?: unknown } | null | undefined)?.inspection;
  if (inspection === null || inspection === undefined || typeof inspection !== "object") {
    return { kind: "conflicted", reason: REVIEW_REASON_RESULT_MISSING };
  }
  const status = (inspection as Record<string, unknown>)["status"];
  if (status === "passed" || status === "failed") return { kind: "applied" };
  if (status === "cancelled") {
    return { kind: "conflicted", reason: REVIEW_REASON_INSPECTION_CANCELLED };
  }
  return { kind: "conflicted", reason: REVIEW_REASON_RESULT_UNRECOGNIZED };
}

// ---------------------------------------------------------------------------
// Queue semantics per operation kind
// ---------------------------------------------------------------------------

/**
 * A LAST-WRITE-WINS operation: re-doing it with different values SUPERSEDES the
 * still-queued earlier attempt instead of stacking a second mutation behind it.
 *
 * Only the count record qualifies, and only because §5 row 40 says so in the
 * contract itself — "identical re-record = no-op; a differing one overwrites
 * while in_progress". Nothing else on this list is idempotent in that sense: a
 * second cut is a second physical cut, a second receive is a second receipt.
 * Superseding one of those would silently drop work an operator performed.
 *
 * IMPORTANT: superseding mints a NEW client key for the replacement (the
 * engine does this). Re-using the old key with a new body is precisely what
 * §9.2 answers 409 `IDEMPOTENCY_KEY_REUSE` to.
 */
export function isSupersedable(op: FloorOp): boolean {
  return op.kind === "count_record";
}

/**
 * The identity a double-tap collides on. Two enqueues sharing a dedupe key and
 * carrying the SAME body are one operation (the finger bounced, the scanner
 * fired twice); the engine drops the second.
 *
 * `undefined` = never deduped. A scan is deliberately in that class: scanning
 * the same bobbin twice is a real, meaningful pair of events (§3.5's attribution
 * ladder is a SEQUENCE), and collapsing them would corrupt the ladder.
 */
export function dedupeKeyOf(op: FloorOp): string | undefined {
  switch (op.kind) {
    case "count_record":
      return `count_record:${op.countId}:${op.lineId}`;
    case "count_submit":
      return `count_submit:${op.countId}`;
    case "count_found":
      return `count_found:${op.countId}:${op.body.stock_unit_id ?? op.body.product_id ?? ""}:${op.body.location_id}`;
    case "transfer_receive":
      return undefined;
    case "cut":
      return undefined;
    case "remeasure":
      return undefined;
    case "scan":
      return undefined;
    case "production_event":
      // Undefined for EVERY production kind (D-080, §3.4): a second tap is a
      // second PHYSICAL fact — two qty reports are two crates, two Starts are
      // a crew join (§3.2). Collapsing them client-side would erase work; the
      // server's UNIQUE (tenant_id, client_event_id) is the only dedupe, and
      // it dedupes REPLAYS of one tap, never two taps.
      return undefined;
    case "waste_event":
    case "waste_remnant":
      // Same rule as production (spec 05 §9.1): two captures are two physical
      // facts; the server's client_event_id UNIQUE dedupes replays of one tap.
      return undefined;
    case "quality_result":
      // A check item has ONE verdict (spec 07 §2.6's UNIQUE identity), so a
      // bounced finger on Pass is not two verdicts — it is one, and the second
      // enqueue collapses onto the first when the bodies match.
      //
      // NOT `isSupersedable`, deliberately: a DIFFERING re-record is a
      // correction the operator made, and it stacks BEHIND the first rather
      // than replacing it. The server's UPSERT makes the last one win in FIFO
      // order — the same answer superseding would have produced — while the
      // queue keeps both taps' evidence photos owned by a live record. A
      // superseded record's blobs would be orphaned on the device (the engine
      // drops the row, not the bytes), and dropping evidence to save one round
      // trip is the wrong trade on the screen whose whole output is evidence.
      return `quality_result:${op.inspectionId}:${op.body.check_item_id}`;
    case "quality_complete":
      // §3.4 completes an inspection ONCE. Two taps are one intent.
      return `quality_complete:${op.inspectionId}`;
    case "shipment_open":
      // One open shipment per order is DDL (0063's partial UNIQUE — D-286
      // ruling 3): a bounced "Start picking" is one intent, and a second
      // enqueue would only answer 409 SHIPMENT_OPEN.
      return `shipment_open:${op.orderId}`;
    case "shipment_pick":
      // ONE live pick per (unit, line) on a shipment is the server's own rule
      // (0063's partial UNIQUE → 409 UNIT_ALREADY_PICKED), so a bounced
      // Confirm with the SAME body collapses onto the first. A DIFFERING
      // re-pick of the same unit is NOT superseded: it stacks behind the
      // first, the server refuses it, and the tray's sentence says exactly
      // what the remedy is — undo, then re-pick the total (§4.8).
      return `shipment_pick:${op.shipmentId}:${op.body.stock_unit_id}:${op.body.sales_order_line_id}`;
    case "shipment_unpick":
      return `shipment_unpick:${op.shipmentId}:${op.lineId}`;
    case "shipment_pack":
      return `shipment_pack:${op.shipmentId}`;
    case "shipment_ship":
      // Two taps on Ship are one intent — and the server would answer the
      // second 409 SHIPMENT_NOT_PACKED anyway.
      return `shipment_ship:${op.shipmentId}`;
  }
}

/** The entity a parked item points at, so the tray's "Open" is never a dead end. */
export interface OpTarget {
  /**
   * `ship` (PR-D3) is the pick screen, addressed by ORDER id — every shipment
   * op carries `orderId` for exactly this. The tray renders an Open link per
   * route kind it knows; a kind it does not know yet renders no link, which is
   * an absence, never a dead control.
   */
  readonly route: "unit" | "count" | "transfer" | "inspection" | "ship" | "none";
  readonly id: string;
}

export function opTarget(op: FloorOp): OpTarget {
  switch (op.kind) {
    case "cut":
    case "remeasure":
      return { route: "unit", id: op.unitId };
    case "count_record":
    case "count_found":
    case "count_submit":
      return { route: "count", id: op.countId };
    case "transfer_receive":
      return { route: "transfer", id: op.transferId };
    case "scan":
      return { route: "none", id: "" };
    case "production_event":
      // No floor route to point at from THIS sub-step: the op-run screen is
      // the screens sub-step's surface, and a review row's only action is the
      // confirmed dismiss (the office resolves it on S7, §2.17) — an "Open"
      // that landed on a station screen would invite re-doing work the server
      // already persisted into conflicts.
      return { route: "none", id: "" };
    case "waste_event":
    case "waste_remnant":
      // The capture screens are context-anchored (a station, a scanned unit);
      // by tray time that context is gone, and the tray row's identity string
      // carries the physical description instead.
      return { route: "none", id: "" };
    case "quality_result":
    case "quality_complete":
      // UNLIKE the two above, a quality tap has a durable, addressable subject:
      // the inspection. §9.1's whole recovery is "look at what the server has
      // and decide" — "Completed by {name} while you were offline" is only
      // actionable if the operator can get to the inspection from the tray.
      return { route: "inspection", id: op.inspectionId };
    case "shipment_open":
    case "shipment_pick":
    case "shipment_unpick":
    case "shipment_pack":
    case "shipment_ship":
      // The pick screen (§4.8) is the subject: §9.10's recovery for a parked
      // pick is "open it and re-pick with the fresh remaining", which needs a
      // way from the tray to `/floor/ship/{orderId}`.
      return { route: "ship", id: op.orderId };
  }
}

/**
 * i18n key + interpolation for the one-line description an operator reads in
 * the pending list and the needs-attention tray. Returning KEY + PARAMS rather
 * than a string keeps the engine free of i18next and keeps every string in the
 * gated dictionary.
 */
export interface OpLabel {
  readonly key: string;
  readonly params: Readonly<Record<string, string>>;
}

export function opLabel(op: FloorOp): OpLabel {
  switch (op.kind) {
    case "scan":
      return { key: "inventory.floor.op.scan", params: { payload: op.body.scanned_payload } };
    case "cut":
      return {
        key: "inventory.floor.op.cut",
        params: { pieces: String(op.body.pieces.length) },
      };
    case "remeasure":
      return {
        key: "inventory.floor.op.remeasure",
        params: { measure: op.body.entered_measure },
      };
    case "count_record":
      return {
        key: "inventory.floor.op.countRecord",
        params: { value: op.body.counted_measure ?? op.body.counted_qty_base ?? "" },
      };
    case "count_found":
      return { key: "inventory.floor.op.countFound", params: {} };
    case "count_submit":
      return { key: "inventory.floor.op.countSubmit", params: {} };
    case "transfer_receive":
      return {
        key: "inventory.floor.op.transferReceive",
        params: { lines: String(op.body.lines.length) },
      };
    case "production_event":
      return productionEventLabel(op.event);
    case "waste_event":
      return {
        key: "inventory.floor.op.wasteCapture",
        params: { qty: op.body.qty, uom: op.body.uom },
      };
    case "waste_remnant":
      return {
        key: "inventory.floor.op.remnantRegister",
        params: { length: op.body.length_m },
      };
    case "quality_result":
      // The MEASURED VALUE where there is one, else the verdict. A tray row
      // reading "Check result" three times over is the puzzle the identity
      // string exists to prevent, and the number is what the operator argues
      // about when they come back to it.
      return {
        key: "inventory.floor.op.qualityResult",
        params: { value: op.body.measured_value ?? op.body.result ?? "" },
      };
    case "quality_complete":
      return { key: "inventory.floor.op.qualityComplete", params: {} };
    // Module 09's verbs, in the words the operator pressed (the sales
    // dictionary's floor block — `sales.floor.ship.*`, one owner per file). The
    // physical identity ("SHP-2026-00012 · E2E-1645 · 400 m") rides
    // `OutboxItem.identity`, captured at tap by the pick screen, so a tray
    // row reads "Confirm pick · SHP-2026-00012 · E2E-1645 · 400 m".
    case "shipment_open":
      return { key: "sales.floor.ship.open", params: {} };
    case "shipment_pick":
      return { key: "sales.floor.ship.confirm", params: {} };
    case "shipment_unpick":
      return { key: "sales.floor.ship.unpick", params: {} };
    case "shipment_pack":
      return { key: "sales.floor.ship.pack", params: {} };
    case "shipment_ship":
      return { key: "sales.floor.ship.ship", params: {} };
  }
}

/** A payload field as display text — "" for anything else, so a malformed
 * record degrades to a thin row, never a thrown render. */
function payloadStr(payload: Readonly<Record<string, unknown>>, key: string): string {
  const value = payload[key];
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

/**
 * One label builder per §5.1 kind — EXHAUSTIVE over the wire union on purpose
 * (the S7 raw-enum fix): the tray rendered the five 04-C kinds through the
 * raw-name fallback long after their builders shipped, because a `switch` with
 * a `default` cannot tell "future kind" from "forgotten kind". A twelfth §5.1
 * kind now fails typecheck HERE (and in the office S7 table, which keeps the
 * same guard) instead of silently reading as a wire token on the floor.
 */
const PRODUCTION_EVENT_LABELS: Record<
  ProductionEventBody["event_type"],
  (payload: Readonly<Record<string, unknown>>) => OpLabel
> = {
  op_started: () => ({ key: "inventory.floor.op.opStarted", params: {} }),
  op_paused: () => ({ key: "inventory.floor.op.opPaused", params: {} }),
  op_resumed: () => ({ key: "inventory.floor.op.opResumed", params: {} }),
  op_completed: () => ({ key: "inventory.floor.op.opCompleted", params: {} }),
  qty_reported: (payload) => ({
    key: "inventory.floor.op.qtyReported",
    params: { good: payloadStr(payload, "qty_good"), scrap: payloadStr(payload, "qty_scrap") },
  }),
  material_issued: (payload) => {
    // D-081: a NEGATIVE qty on this kind is the RETURN — the tray must say
    // "return", not render a minus sign as if the issue itself went wrong.
    const qty = payloadStr(payload, "qty");
    if (qty.startsWith("-")) {
      return {
        key: "inventory.floor.op.materialReturned",
        params: { qty: qty.slice(1), uom: payloadStr(payload, "uom") },
      };
    }
    return {
      key: "inventory.floor.op.materialIssued",
      params: { qty, uom: payloadStr(payload, "uom") },
    };
  },
  downtime_started: () => ({ key: "inventory.floor.op.downtimeStarted", params: {} }),
  downtime_ended: () => ({ key: "inventory.floor.op.downtimeEnded", params: {} }),
  calibration_recorded: (payload) => ({
    key: "inventory.floor.op.calibrationRecorded",
    params: {
      claimed: payloadStr(payload, "machine_claimed_qty"),
      measured: payloadStr(payload, "measured_qty"),
      uom: payloadStr(payload, "uom"),
    },
  }),
  // Direction is the §2.14 point of the row; the words are D-085's shared
  // sentences, compressed to the tray's one-line grammar.
  serial_lot_captured: (payload) => ({
    key:
      payload["direction"] === "consumed"
        ? "inventory.floor.op.serialConsumed"
        : "inventory.floor.op.serialProduced",
    params: { qty: payloadStr(payload, "qty"), uom: payloadStr(payload, "uom") },
  }),
  defect_handoff_flagged: (payload) => ({
    key: "inventory.floor.op.defectFlagged",
    params: { qty: payloadStr(payload, "qty_defective") },
  }),
};

/**
 * Labels for the §5.1 production kinds. They live beside the module-06 op
 * labels because the QUEUE is one queue (D-048/D-054) and the tray is one
 * tray: the key namespace follows the engine that renders it, not the module
 * that owns the projection. The physical identity ("WO-2026-00341 · twisting")
 * is `OutboxItem.identity`, captured at tap by the screen.
 */
function productionEventLabel(event: ProductionEventBody): OpLabel {
  // `?? {}` because this label is rendered from records read back out of
  // IndexedDB, which an OLDER build of the app wrote. The type says `payload`
  // is always there and the type is right about anything this build enqueues;
  // it is not a guarantee about bytes already on a device. Without the guard a
  // single malformed queued record throws inside render and takes down the
  // whole op screen or the whole tray — the two screens whose entire job is to
  // account for queued work — and it takes them down for the operator who has
  // the most of it. A label of "" is a degraded row; a thrown TypeError is a
  // white screen with a shift's work behind it.
  // The cast to `| undefined` is the load-bearing half: the declared type says
  // `payload` is always there and TypeScript is right about anything THIS build
  // enqueues, which is why the lint rule would otherwise call the `??` dead.
  // The claim being made here is about bytes an OLDER build left on the device.
  const payload = (event.payload as Record<string, unknown> | undefined) ?? {};
  // The RUNTIME lookup stays guarded even though the record above is total:
  // the same old-build/new-build bytes the `?? {}` exists for can carry a kind
  // this build does not know. Such a record still deserves a readable row
  // naming itself — degraded visibly, never blank.
  const build = (
    PRODUCTION_EVENT_LABELS as Partial<
      Record<string, (payload: Readonly<Record<string, unknown>>) => OpLabel>
    >
  )[event.event_type];
  return build === undefined
    ? { key: "inventory.floor.op.productionEvent", params: { type: event.event_type } }
    : build(payload);
}
